import { Injectable } from 'framework/di';

import { AppService } from '../../app/app.service';
import { RecommendationService } from '../../recommendation/recommendation.service';
import type {
  CartDrawerRecommendationTemplateData,
  CartDrawerWidgetDesignSettings,
  FormattedProduct,
  FormattedVariant,
  RawRecommendationProduct,
  RawRecommendationVariant,
} from './model';

interface CartSettings {
  enableWidgetRecommendation?: boolean;
  widgetRecommendationId?: string;
}

/**
 * Service for preparing cart-drawer recommendation data.
 *
 * This service acts as a **shared renderer** that bridges RecommendationService
 * to consumers (CartService for Phase 1, ThemeAppBlock for Phase 2).
 *
 * ## Architecture
 * ```
 * RecommendationService (core)
 *         ↓
 * CartDrawerRecommendationService (shared renderer)
 *         ↓
 * CartService
 * ```
 *
 * ## Key Responsibilities
 * - Check if cart-drawer recommendation is enabled
 * - Fetch recommendation products via RecommendationService
 * - Format products/variants for template rendering (price formatting)
 * - Provide data structure ready for Liquid template
 *
 * ## Extension Points for TS Team
 * - `formatPrice()` - Customize price formatting
 * - `formatProductsForTemplate()` - Customize product transformation
 * - `formatVariantsForTemplate()` - Customize variant transformation
 * - `fetchRecommendationProducts()` - Add custom filtering/sorting
 *
 * @example
 * Extension point for TS team - custom price formatting:
 * ```javascript
 * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
 *   return class extends Base {
 *     formatPrice(price, showCurrencyCodes) {
 *       // Custom formatting with EUR currency
 *       return `€${(price / 100).toFixed(2)}`;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Extension point - filter out-of-stock products:
 * ```javascript
 * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
 *   return class extends Base {
 *     formatProductsForTemplate(products, showCurrencyCodes) {
 *       // Filter out products with no available variants
 *       const availableProducts = products.filter(p =>
 *         p.variants?.some(v => v.available)
 *       );
 *       return super.formatProductsForTemplate(availableProducts, showCurrencyCodes);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class CartDrawerRecommendationService {
  constructor(
    protected recommendationService: RecommendationService,
    protected appService: AppService
  ) {}

  /**
   * Check if cart-drawer recommendation is enabled in TAE config.
   *
   * Checks for:
   * - `cart.enableWidgetRecommendation` = true
   * - `cart.widgetRecommendationId` is set
   *
   * @returns True if cart-drawer recommendation is enabled
   *
   * @example
   * Override to add additional enable conditions:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     isEnabled() {
   *       // Disable recommendations for specific customer tags
   *       if (window.__boostCustomerTags?.includes('wholesale')) return false;
   *       return super.isEnabled();
   *     }
   *   };
   * });
   * ```
   */
  isEnabled(): boolean {
    const cartSettings = this.getCartSettings();

    return Boolean(
      cartSettings?.enableWidgetRecommendation && cartSettings?.widgetRecommendationId
    );
  }

  /**
   * Get the widget ID from cart settings.
   *
   * @returns Widget ID string (e.g., "cartdrawer-408504") or null if not configured
   *
   * @example
   * Override to use a different widget ID per page type:
   * ```typescript
   * getWidgetId() {
   *   if (window.location.pathname === '/cart') return 'cart-page-rec-1';
   *   return super.getWidgetId();
   * }
   * ```
   */
  getWidgetId(): string | null {
    const cartSettings = this.getCartSettings();
    return cartSettings?.widgetRecommendationId || null;
  }

  /**
   * Get cart-drawer recommendation data ready for template rendering.
   *
   * This is the main method consumers (CartService, ThemeAppBlock) should call.
   * It handles the full flow: validate → fetch → format → return.
   *
   * @param showCurrencyCodes - Whether to show currency codes in formatted prices
   * @returns Promise resolving to template data, or null if not enabled/error
   *
   * @example
   * Usage in CartService:
   * ```typescript
   * const recData = await this.cartDrawerRecommendationService
   *   .getCartDrawerRecommendationData(showCurrencyCodes);
   * if (recData) {
   *   templateData = { ...cartData, ...recData };
   * }
   * ```
   */
  async getCartDrawerRecommendationData(
    showCurrencyCodes: boolean
  ): Promise<CartDrawerRecommendationTemplateData | null> {
    if (!this.isEnabled()) {
      return null;
    }

    const widgetId = this.getWidgetId();
    if (!widgetId) {
      return null;
    }

    try {
      // Get widget config from TAE
      const widgetConfig = this.getWidgetConfig(widgetId);
      if (!widgetConfig) {
        this.appService.logger.warn('Cart-drawer recommendation widget config not found', {
          widgetId,
        });
        return null;
      }

      const { widgetName, widgetDesignSettings } = widgetConfig;

      // Fetch products via RecommendationService
      const rawProducts = await this.fetchRecommendationProducts(widgetId);

      if (!rawProducts || rawProducts.length === 0) {
        this.appService.logger.debug('No recommendation products returned for cart-drawer');
        return null;
      }

      // Format products for template
      const formattedProducts = this.formatProductsForTemplate(rawProducts, showCurrencyCodes);

      return {
        widgetId,
        widgetName,
        widgetDesignSettings,
        products: formattedProducts,
        request: {
          previewMode: this.isPreviewMode(),
        },
      };
    } catch (error) {
      this.appService.logger.error('Failed to get cart-drawer recommendation data', error);
      return null;
    }
  }

  /**
   * Whether the widget is in preview mode (e.g., TAE Admin preview).
   * Override to check TAE preview state or enable preview for specific conditions.
   *
   * @returns True if the widget should render in preview mode
   *
   * @example
   * Override to enable preview mode based on URL parameter:
   * ```typescript
   * protected isPreviewMode() {
   *   const urlParams = new URLSearchParams(window.location.search);
   *   return urlParams.get('preview') === 'true';
   * }
   * ```
   */
  protected isPreviewMode(): boolean {
    return false;
  }

  /**
   * Get cart settings from TAE config.
   *
   * Override this method to provide custom cart settings or merge
   * additional shop-specific configuration.
   *
   * @returns Cart settings object or undefined if not configured
   *
   * @example
   * Override to merge additional settings:
   * ```typescript
   * protected getCartSettings() {
   *   const settings = super.getCartSettings();
   *   return { ...settings, enableWidgetRecommendation: true };
   * }
   * ```
   */
  protected getCartSettings(): CartSettings | undefined {
    return this.appService.themeSettings.cart as CartSettings | undefined;
  }

  /**
   * Get widget configuration from TAE recommendationWidgets.
   *
   * Override this method to provide custom widget configurations or
   * fallback values for specific shops.
   *
   * @param widgetId - The widget ID to look up
   * @returns Widget config with name and design settings, or null if not found
   *
   * @example
   * Override to provide a custom widget title:
   * ```typescript
   * protected getWidgetConfig(widgetId: string) {
   *   const config = super.getWidgetConfig(widgetId);
   *   if (config) {
   *     config.widgetName = 'You May Also Like';
   *   }
   *   return config;
   * }
   * ```
   */
  protected getWidgetConfig(widgetId: string): {
    widgetName: string;
    widgetDesignSettings: CartDrawerWidgetDesignSettings;
  } | null {
    try {
      const config = this.recommendationService.getRecommendationWidgetConfig(widgetId);
      return {
        widgetName: config.widgetName || 'Recommendations',
        widgetDesignSettings: (config.widgetDesignSettings || {}) as CartDrawerWidgetDesignSettings,
      };
    } catch {
      return null;
    }
  }

  /**
   * Fetch recommendation products from API via RecommendationService.
   *
   * Override this method to:
   * - Add custom filtering before/after fetch
   * - Pass additional context (cart product IDs, customer ID)
   * - Implement caching
   *
   * @param widgetId - The widget ID
   * @returns Promise resolving to raw products array
   *
   * @example
   * Override to filter products or limit results per shop:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     async fetchRecommendationProducts(widgetId) {
   *       const products = await super.fetchRecommendationProducts(widgetId);
   *       // Limit to 4 products and exclude out-of-stock
   *       return products
   *         .filter(p => p.available !== false)
   *         .slice(0, 4);
   *     }
   *   };
   * });
   * ```
   */
  protected async fetchRecommendationProducts(
    widgetId: string
  ): Promise<RawRecommendationProduct[]> {
    const products = await this.recommendationService.getRecommendationWidgetProducts(widgetId);
    const rawProducts = products as unknown as RawRecommendationProduct[];

    return this.filterAvailableProducts(rawProducts);
  }

  /**
   * Format products for template rendering.
   *
   * Transforms raw API products into template-ready format with:
   * - Formatted prices (using store currency settings)
   * - Formatted variant prices
   * - Consistent structure for Liquid template
   *
   * @param products - Raw products from API
   * @param showCurrencyCodes - Whether to show currency codes
   * @returns Formatted products array
   *
   * @example
   * Override to add custom product properties for template rendering:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     formatProductsForTemplate(products, showCurrencyCodes) {
   *       return super.formatProductsForTemplate(products, showCurrencyCodes)
   *         .map(product => ({
   *           ...product,
   *           customBadge: product.tags?.includes('new') ? 'New' : null,
   *         }));
   *     }
   *   };
   * });
   * ```
   */
  protected formatProductsForTemplate(
    products: RawRecommendationProduct[],
    showCurrencyCodes: boolean
  ): FormattedProduct[] {
    return products.map((product) => {
      // Get raw prices - parse string or number values from API
      const rawPriceMin = this.parsePrice(product.price_min);
      const rawPriceMax = this.parsePrice(product.price_max);
      const rawCompareAtPriceMin = this.parsePrice(product.compare_at_price_min);
      const rawCompareAtPriceMax = this.parsePrice(product.compare_at_price_max);
      const formattedVariants = this.formatVariantsForTemplate(
        product.variants || [],
        showCurrencyCodes
      );
      const firstVariantRawPrice =
        formattedVariants.length > 0 ? formattedVariants[0].price_raw : null;
      const initialRawPrice = firstVariantRawPrice ?? rawPriceMin;

      // Format prices for display
      const priceMinFormatted = this.formatPrice(initialRawPrice, showCurrencyCodes);
      const priceMaxFormatted = this.formatPrice(rawPriceMax, showCurrencyCodes);
      // Use explicit null check (not truthy) so price of 0 is formatted correctly
      const compareAtPriceMinFormatted =
        rawCompareAtPriceMin != null
          ? this.formatPrice(rawCompareAtPriceMin, showCurrencyCodes)
          : null;
      const compareAtPriceMaxFormatted =
        rawCompareAtPriceMax != null
          ? this.formatPrice(rawCompareAtPriceMax, showCurrencyCodes)
          : null;

      return {
        ...product,
        id: product.id,
        handle: product.handle,
        title: product.title,
        // Use formatted prices as the primary values (for templates using {{ price_min }})
        // This ensures currency symbol is displayed on initial render
        price_min: priceMinFormatted,
        price_max: priceMaxFormatted,
        compare_at_price_min: compareAtPriceMinFormatted,
        compare_at_price_max: compareAtPriceMaxFormatted,
        // Also provide _formatted versions for explicit use
        price_min_formatted: priceMinFormatted,
        price_max_formatted: priceMaxFormatted,
        compare_at_price_min_formatted: compareAtPriceMinFormatted,
        compare_at_price_max_formatted: compareAtPriceMaxFormatted,
        // Keep raw numeric values with _raw suffix if needed
        price_min_raw: rawPriceMin,
        price_max_raw: rawPriceMax,
        compare_at_price_min_raw: rawCompareAtPriceMin,
        compare_at_price_max_raw: rawCompareAtPriceMax,
        images_info: product.images_info || [],
        variants: formattedVariants,
      };
    });
  }

  /**
   * Format variants for template rendering.
   *
   * Transforms raw variant data into display-ready format with formatted prices.
   * Override to add custom variant properties or filtering logic.
   *
   * @param variants - Raw variants from product
   * @param showCurrencyCodes - Whether to show currency codes
   * @returns Formatted variants array
   *
   * @example
   * Override to filter out unavailable variants:
   * ```typescript
   * protected formatVariantsForTemplate(variants, showCurrencyCodes) {
   *   const available = variants.filter(v => v.available !== false);
   *   return super.formatVariantsForTemplate(available, showCurrencyCodes);
   * }
   * ```
   */
  protected formatVariantsForTemplate(
    variants: RawRecommendationVariant[],
    showCurrencyCodes: boolean
  ): FormattedVariant[] {
    const sortedVariants = this.sortVariantsByAvailability(variants);

    return sortedVariants.map((variant) => {
      // Get raw price - parse string or number values from API
      const rawPrice = this.parsePrice(variant.price);
      const rawCompareAtPrice = this.parsePrice(variant.compare_at_price);

      // Format prices
      const priceFormatted = this.formatPrice(rawPrice, showCurrencyCodes);
      // Use explicit null check (not truthy) so price of 0 is formatted correctly
      const compareAtPriceFormatted =
        rawCompareAtPrice != null ? this.formatPrice(rawCompareAtPrice, showCurrencyCodes) : null;

      return {
        ...variant,
        id: variant.id,
        title: variant.title,
        // Use formatted price as primary value (for templates using {{ variant.price }})
        // This ensures currency symbol is displayed on initial render
        price: priceFormatted,
        compare_at_price: compareAtPriceFormatted,
        // Also provide _formatted versions for explicit use
        price_formatted: priceFormatted,
        compare_at_price_formatted: compareAtPriceFormatted,
        // Keep raw numeric values with _raw suffix if needed
        price_raw: rawPrice,
        compare_at_price_raw: rawCompareAtPrice,
        available: variant.available ?? true,
        image: variant.image,
      };
    });
  }

  /**
   * Filter out products that are completely out of stock.
   *
   * Removes products where no variants are available (i.e., all variants are OOS
   * with "sell when out of stock" disabled). Products with at least one available
   * variant are kept.
   *
   * Override this method to customize product availability filtering logic.
   *
   * @param products - Raw products from API
   * @returns Products that have at least one available variant
   *
   * @example
   * Override to also filter by custom tags:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     filterAvailableProducts(products) {
   *       return super.filterAvailableProducts(products)
   *         .filter(p => !p.tags?.includes('hidden'));
   *     }
   *   };
   * });
   * ```
   */
  protected filterAvailableProducts(
    products: RawRecommendationProduct[]
  ): RawRecommendationProduct[] {
    return products.filter((product) => {
      // If variants data is available, check if at least one variant is in stock
      if (product.variants && product.variants.length > 0) {
        return product.variants.some((variant) => variant.available !== false);
      }

      // Fallback to product-level available field (via index signature)
      if (product.available !== undefined) {
        return product.available !== false;
      }

      // If no availability info, keep the product (don't over-filter)
      return true;
    });
  }

  /**
   * Sort variants so that available (in-stock) variants come first.
   *
   * This ensures the default variant selection (first in the list) prioritizes
   * in-stock variants, preventing users from being defaulted to an OOS variant.
   *
   * Override this method to customize variant sort order.
   *
   * @param variants - Raw variants from product
   * @returns Variants sorted with available ones first, preserving original order within each group
   *
   * @example
   * Override to also sort by price within each availability group:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     sortVariantsByAvailability(variants) {
   *       return super.sortVariantsByAvailability(variants)
   *         .sort((a, b) => {
   *           if (a.available === b.available) return (a.price || 0) - (b.price || 0);
   *           return 0;
   *         });
   *     }
   *   };
   * });
   * ```
   */
  protected sortVariantsByAvailability(
    variants: RawRecommendationVariant[]
  ): RawRecommendationVariant[] {
    return [...variants].sort((a, b) => {
      const aAvailable = a.available !== false ? 1 : 0;
      const bAvailable = b.available !== false ? 1 : 0;
      return bAvailable - aAvailable;
    });
  }

  /**
   * Parse a price value from API response.
   *
   * The API may return prices as numbers or strings (e.g., "20.0").
   * This method normalizes them to numbers for formatting.
   *
   * @param price - Price value from API (number or string)
   * @returns Parsed number, or null if invalid/empty
   *
   * @example
   * Override to handle a custom price format:
   * ```typescript
   * protected parsePrice(price: unknown) {
   *   // Handle comma-separated decimals (e.g., "19,99")
   *   if (typeof price === 'string' && price.includes(',')) {
   *     return parseFloat(price.replace(',', '.'));
   *   }
   *   return super.parsePrice(price);
   * }
   * ```
   */
  protected parsePrice(price: unknown): number | null {
    if (price === null || price === undefined || price === '') {
      return null;
    }

    // If already a number, return it
    if (typeof price === 'number') {
      return isNaN(price) ? null : price;
    }

    // If string, parse it
    if (typeof price === 'string') {
      const parsed = parseFloat(price);
      return isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  /**
   * Format a price value for display.
   *
   * Uses AppService.formatCurrency for consistent store-wide formatting.
   * Override this method to customize price formatting.
   *
   * @remarks
   * Recommendation API returns prices already in the store's currency unit
   * (e.g., 19.99 for $19.99, 100 for 100 VND), NOT in cents like Shopify Cart API.
   *
   * @param price - Price value from Recommendation API (e.g., 19.99 or 100)
   * @param showCurrencyCodes - Whether to show currency codes (e.g., "USD")
   * @returns Formatted price string (e.g., "$19.99" or "$19.99 USD"), or empty string if price is null/undefined
   *
   * @example
   * Override to apply shop-specific rounding or custom currency display:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationService', (Base) => {
   *   return class extends Base {
   *     formatPrice(price, showCurrencyCodes) {
   *       if (price != null) price = Math.round(price); // Round to whole number
   *       return super.formatPrice(price, showCurrencyCodes);
   *     }
   *   };
   * });
   * ```
   */
  protected formatPrice(price: number | undefined | null, showCurrencyCodes: boolean): string {
    if (price === null || price === undefined) {
      return '';
    }

    const priceValue = typeof price === 'number' ? price : 0;
    const taeConfig = this.appService.TAEAppConfig;

    // Get price display settings from theme config
    const priceSettings =
      taeConfig.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

    const showCentAsSuperscript = priceSettings.showCentAsSuperscript;
    const removePriceDecimal = Boolean(
      (priceSettings as Record<string, unknown>).removePriceDecimal
    );

    // Use money_format_with_currency if showCurrencyCodes, otherwise use money_format
    const formatString = showCurrencyCodes
      ? taeConfig.shop?.money_format_with_currency || taeConfig.shop?.money_format || '${{amount}}'
      : taeConfig.shop?.money_format || '${{amount}}';

    return this.appService.formatCurrency(priceValue, {
      format: formatString,
      showCurrencyCodes,
      showCentAsSuperscript,
      removePriceDecimal,
    });
  }
}
